/**
 * AdminProductReportTab - FlexiFunnels-style Product Report Dashboard
 * Comprehensive business metrics with date filtering and export capabilities.
 */
import { useState, useEffect, useCallback } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AdminFunnelReport } from "@/components/admin/AdminFunnelReport";
import {
  Loader2, FileDown, Calendar, TrendingUp, Users, Download,
  ShoppingCart, DollarSign, Target, Clock, RefreshCw, Eye,
  UserPlus, LogIn, Zap, Percent, BarChart3, ChevronDown
} from "lucide-react";

// ─── Date Filter Options ──────────────────────────────────────────────
const DATE_FILTERS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "all_time", label: "All Time" },
];

// ─── Metric Card Component ────────────────────────────────────────────
function MetricCard({ label, value, icon: Icon, color, subValue, info }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow" data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        {info && (
          <span className="text-[10px] text-slate-400 cursor-help" title={info}>ⓘ</span>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mb-1 font-medium">{label}</p>
      <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
        {value}
      </p>
      {subValue && (
        <p className="text-[10px] text-slate-400 mt-0.5">{subValue}</p>
      )}
    </div>
  );
}

// ─── Section Header Component ─────────────────────────────────────────
function SectionHeader({ title, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <h3 className="text-sm font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
        {title}
      </h3>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────
export function AdminProductReportTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("last_30_days");
  const [exporting, setExporting] = useState(false);
  const [showDateMenu, setShowDateMenu] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.productReport({ date_filter: dateFilter });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load product report");
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async (type, format) => {
    setExporting(true);
    try {
      const res = await adminAPI.exportProductReport(type, format, dateFilter);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `product_report_${type}_${dateFilter}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${type} report to ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString("en-IN");
  };

  const formatCurrency = (num) => {
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatPercent = (num) => `${num}%`;

  const formatTTFV = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    if (minutes < 1440) return `${Math.round(minutes / 60 * 10) / 10} hr`;
    return `${Math.round(minutes / 1440 * 10) / 10} days`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="product-report-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Failed to load product report</p>
        <Button variant="outline" size="sm" onClick={loadReport} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  const { traffic, activation, product_experience, conversion, revenue, optional, date_range } = data;

  return (
    <div className="space-y-6" data-testid="product-report-tab">
      {/* NEW: Internal Analytics Funnel — independent of Meta */}
      <AdminFunnelReport />

      <div className="h-px bg-slate-200" />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
            Product Report
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            FlexiFunnels-style business metrics for Studio Pass Pro
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Date Filter */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDateMenu(!showDateMenu)}
              className="h-9 text-xs gap-2"
              data-testid="date-filter-btn"
            >
              <Calendar className="h-3.5 w-3.5" />
              {DATE_FILTERS.find(d => d.value === dateFilter)?.label || dateFilter}
              <ChevronDown className="h-3 w-3" />
            </Button>
            
            {showDateMenu && (
              <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                {DATE_FILTERS.map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => {
                      setDateFilter(filter.value);
                      setShowDateMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${
                      dateFilter === filter.value ? "bg-slate-100 font-medium" : ""
                    }`}
                    data-testid={`date-filter-${filter.value}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Refresh */}
          <Button
            variant="outline"
            size="sm"
            onClick={loadReport}
            disabled={loading}
            className="h-9 text-xs"
            data-testid="refresh-report-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      
      {/* Date Range Badge */}
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
          {date_range?.label || dateFilter}
        </Badge>
        <span className="text-[10px] text-slate-400">
          Generated at {new Date(data.generated_at).toLocaleString("en-IN")}
        </span>
      </div>
      
      {/* Export Buttons */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
        <span className="text-xs font-medium text-slate-600 mr-2">Export:</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleExport("summary", "csv")}
          disabled={exporting}
          className="h-7 text-[11px] gap-1"
          data-testid="export-summary-csv"
        >
          <FileDown className="h-3 w-3" /> Summary CSV
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleExport("summary", "xlsx")}
          disabled={exporting}
          className="h-7 text-[11px] gap-1"
          data-testid="export-summary-xlsx"
        >
          <FileDown className="h-3 w-3" /> Summary Excel
        </Button>
        <span className="text-slate-300 mx-1">|</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleExport("detailed", "csv")}
          disabled={exporting}
          className="h-7 text-[11px] gap-1"
          data-testid="export-detailed-csv"
        >
          <FileDown className="h-3 w-3" /> Detailed CSV
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleExport("detailed", "xlsx")}
          disabled={exporting}
          className="h-7 text-[11px] gap-1"
          data-testid="export-detailed-xlsx"
        >
          <FileDown className="h-3 w-3" /> Detailed Excel
        </Button>
        {exporting && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400 ml-2" />}
      </div>
      
      {/* ─── TRAFFIC / REACH ───────────────────────────────────────────── */}
      <section>
        <SectionHeader title="Traffic / Reach" icon={Eye} color="bg-purple-100 text-purple-600" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            label="App Page Views"
            value={formatNumber(traffic.app_page_views)}
            icon={Eye}
            color="bg-purple-100 text-purple-600"
            info="Total page views on the app"
          />
          <MetricCard
            label="Unique App Visitors"
            value={formatNumber(traffic.unique_app_visitors)}
            icon={Users}
            color="bg-indigo-100 text-indigo-600"
            info="Unique visitors to the app"
          />
        </div>
      </section>
      
      {/* ─── LOGIN / ACTIVATION ────────────────────────────────────────── */}
      <section>
        <SectionHeader title="Login / Activation" icon={LogIn} color="bg-blue-100 text-blue-600" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            label="OTP Login Attempts"
            value={formatNumber(activation.otp_login_attempts)}
            icon={Zap}
            color="bg-amber-100 text-amber-600"
            info="Total OTP requests sent"
          />
          <MetricCard
            label="Successful Logins"
            value={formatNumber(activation.successful_logins)}
            icon={LogIn}
            color="bg-green-100 text-green-600"
            info="Successful OTP verifications"
          />
          <MetricCard
            label="Unique Logged-in Users"
            value={formatNumber(activation.unique_logged_in_users)}
            icon={UserPlus}
            color="bg-blue-100 text-blue-600"
            info="Distinct users who logged in"
          />
        </div>
      </section>
      
      {/* ─── PRODUCT EXPERIENCE ────────────────────────────────────────── */}
      <section>
        <SectionHeader title="Product Experience" icon={Download} color="bg-cyan-100 text-cyan-600" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricCard
            label="Users With First Download"
            value={formatNumber(product_experience.users_with_first_download)}
            icon={Download}
            color="bg-cyan-100 text-cyan-600"
            info="Users who completed at least one download"
          />
          <MetricCard
            label="Free Download Maxed"
            value={formatNumber(product_experience.free_download_maxed_users)}
            icon={Target}
            color="bg-orange-100 text-orange-600"
            info="Users who used all free sample downloads"
          />
          <MetricCard
            label="Upgrade Modal Views"
            value={formatNumber(product_experience.upgrade_modal_views)}
            icon={TrendingUp}
            color="bg-pink-100 text-pink-600"
            info="Times upgrade modal was shown"
          />
          <MetricCard
            label="Checkout Views"
            value={formatNumber(product_experience.checkout_views)}
            icon={ShoppingCart}
            color="bg-violet-100 text-violet-600"
            info="Total checkout initiations"
          />
          <MetricCard
            label="Unique Checkout Views"
            value={formatNumber(product_experience.unique_checkout_views)}
            icon={ShoppingCart}
            color="bg-fuchsia-100 text-fuchsia-600"
            info="Unique users who reached checkout"
          />
        </div>
      </section>
      
      {/* ─── CONVERSION ────────────────────────────────────────────────── */}
      <section>
        <SectionHeader title="Conversion Funnel" icon={TrendingUp} color="bg-emerald-100 text-emerald-600" />
        
        {/* Primary Funnel Conversion Rates - in natural funnel order */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Login Conversion (Visitor → Login) */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-login-conversion">
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100 text-blue-600">
                <LogIn className="h-5 w-5" />
              </div>
              <span className="text-[10px] text-slate-400 cursor-help" title="Successful Logins ÷ Unique Visitors">ⓘ</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-1 font-medium">Visitor → Login</p>
            <p className="text-2xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {formatPercent(conversion.login_conversion_rate)}
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              {conversion.successful_logins} / {conversion.unique_app_visitors} visitors logged in
            </p>
          </div>
          
          {/* Login → Download Activation */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-login-to-download-activation">
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-100 text-cyan-600">
                <Download className="h-5 w-5" />
              </div>
              <span className="text-[10px] text-slate-400 cursor-help" title="Users with first download ÷ Successful logins">ⓘ</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-1 font-medium">Login → Download</p>
            <p className="text-2xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {formatPercent(conversion.login_to_download_activation_rate)}
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              {conversion.users_with_first_download} / {conversion.successful_logins} users downloaded their first image
            </p>
          </div>
          
          {/* Activated → Paid Conversion */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-activated-to-paid-conversion">
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-100 text-emerald-600">
                <TrendingUp className="h-5 w-5" />
              </div>
              <span className="text-[10px] text-slate-400 cursor-help" title="Paid Upgrades ÷ Users who completed at least one download">ⓘ</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-1 font-medium">Activated → Paid</p>
            <p className="text-2xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {formatPercent(conversion.activated_to_paid_conversion_rate)}
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              {conversion.paid_upgrades} / {conversion.users_with_first_download} activated users upgraded
            </p>
          </div>
          
          {/* Maxed Free → Paid Conversion */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-maxed-free-to-paid-conversion">
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-100 text-amber-600">
                <Target className="h-5 w-5" />
              </div>
              <span className="text-[10px] text-slate-400 cursor-help" title="Paid Upgrades ÷ Users who maxed free sample downloads">ⓘ</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-1 font-medium">Maxed Free → Paid</p>
            <p className="text-2xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {formatPercent(conversion.maxed_free_to_paid_conversion_rate)}
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              {conversion.paid_upgrades} / {conversion.users_maxed_free} users upgraded
            </p>
          </div>
        </div>
        
        {/* Additional Conversion Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {/* Visitor → Paid (end-to-end) */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-visitor-to-paid-conversion">
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-100 text-indigo-600">
                <Users className="h-5 w-5" />
              </div>
              <span className="text-[10px] text-slate-400 cursor-help" title="Paid Upgrades ÷ Unique App Visitors (end-to-end)">ⓘ</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-1 font-medium">Visitor → Paid</p>
            <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {formatPercent(conversion.visitor_to_paid_conversion_rate)}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              {conversion.paid_upgrades} / {conversion.unique_app_visitors} visitors upgraded
            </p>
          </div>
          
          {/* Upgrade Intent Rate */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="metric-upgrade-intent-rate">
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100 text-purple-600">
                <Eye className="h-5 w-5" />
              </div>
              <span className="text-[10px] text-slate-400 cursor-help" title="Unique upgrade modal users ÷ Users who downloaded at least 1 image">ⓘ</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-1 font-medium">Upgrade Intent Rate</p>
            <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {formatPercent(conversion.upgrade_intent_rate)}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              {conversion.unique_upgrade_modal_users} of {conversion.users_with_first_download} showed intent
            </p>
          </div>
          
          <MetricCard
            label="First Download Rate"
            value={formatPercent(conversion.first_download_rate)}
            icon={Percent}
            color="bg-cyan-100 text-cyan-600"
            subValue="Downloads / Registered"
            info="Users with download ÷ registered users"
          />
          <MetricCard
            label="Checkout Conversion"
            value={formatPercent(conversion.checkout_conversion_rate)}
            icon={Percent}
            color="bg-rose-100 text-rose-600"
            subValue="Paid / Checkouts"
            info="Paid upgrades ÷ unique checkout views"
          />
        </div>
        
        {/* Supporting counts row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricCard
            label="Successful Logins"
            value={formatNumber(conversion.successful_logins)}
            icon={LogIn}
            color="bg-blue-100 text-blue-600"
            info="Users who successfully verified OTP"
          />
          <MetricCard
            label="First Downloads"
            value={formatNumber(conversion.users_with_first_download)}
            icon={Download}
            color="bg-cyan-100 text-cyan-600"
            info="Users who downloaded at least once"
          />
          <MetricCard
            label="Users Maxed Free"
            value={formatNumber(conversion.users_maxed_free)}
            icon={Target}
            color="bg-orange-100 text-orange-600"
            info="Users who used all free sample downloads"
          />
          <MetricCard
            label="Paid Upgrades"
            value={formatNumber(conversion.paid_upgrades)}
            icon={TrendingUp}
            color="bg-emerald-100 text-emerald-600"
            info="Successful purchases"
          />
          <MetricCard
            label="Unique Visitors"
            value={formatNumber(conversion.unique_app_visitors)}
            icon={Users}
            color="bg-indigo-100 text-indigo-600"
            info="Unique app visitors"
          />
        </div>
      </section>
      
      {/* ─── REVENUE ───────────────────────────────────────────────────── */}
      <section>
        <SectionHeader title="Revenue" icon={DollarSign} color="bg-green-100 text-green-600" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricCard
            label="Revenue"
            value={formatCurrency(revenue.revenue)}
            icon={DollarSign}
            color="bg-green-100 text-green-600"
            info="Total successful payment amount"
          />
          <MetricCard
            label="Net Revenue"
            value={formatCurrency(revenue.net_revenue)}
            icon={DollarSign}
            color="bg-emerald-100 text-emerald-600"
            info="Revenue after refunds"
          />
          <MetricCard
            label="Avg Order Value"
            value={formatCurrency(revenue.avg_order_value)}
            icon={BarChart3}
            color="bg-yellow-100 text-yellow-600"
            info="Revenue ÷ unique orders"
          />
          <MetricCard
            label="Revenue/Visitor"
            value={formatCurrency(revenue.revenue_per_app_visitor)}
            icon={Users}
            color="bg-indigo-100 text-indigo-600"
            info="Revenue ÷ unique app visitors"
          />
          <MetricCard
            label="Revenue/User"
            value={formatCurrency(revenue.revenue_per_logged_in_user)}
            icon={UserPlus}
            color="bg-purple-100 text-purple-600"
            info="Revenue ÷ unique logged-in users"
          />
        </div>
      </section>
      
      {/* ─── OPTIONAL / HIGH-VALUE ─────────────────────────────────────── */}
      <section className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-6 border border-slate-200">
        <SectionHeader title="Insights & High-Value Metrics" icon={Clock} color="bg-slate-200 text-slate-600" />
        
        {/* TTFV & Re-download */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MetricCard
            label="Avg Time to First Value"
            value={formatTTFV(optional.avg_ttfv_minutes)}
            icon={Clock}
            color="bg-indigo-100 text-indigo-600"
            subValue={`${optional.avg_ttfv_minutes.toFixed(1)} min`}
            info="Average time from registration to first download"
          />
          <MetricCard
            label="Median TTFV"
            value={formatTTFV(optional.median_ttfv_minutes)}
            icon={Clock}
            color="bg-purple-100 text-purple-600"
            subValue={`${optional.median_ttfv_minutes.toFixed(1)} min`}
            info="Median time to first value"
          />
          <MetricCard
            label="Re-download Ratio"
            value={formatPercent(optional.redownload_ratio)}
            icon={RefreshCw}
            color="bg-slate-200 text-slate-600"
            info="Re-downloads ÷ total downloads"
          />
        </div>
        
        {/* Top Lists */}
        <div className="grid md:grid-cols-3 gap-4">
          {/* Top Downloaded Images */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h4 className="text-xs font-semibold text-slate-600 mb-3">Top Downloaded Images</h4>
            {optional.top_downloaded_images.length === 0 ? (
              <p className="text-[11px] text-slate-400">No data</p>
            ) : (
              <div className="space-y-2">
                {optional.top_downloaded_images.map((img, idx) => (
                  <div key={img.public_id} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 truncate flex-1 mr-2">
                      <span className="text-slate-400 mr-1">{idx + 1}.</span>
                      {img.dish_name}
                    </span>
                    <Badge variant="secondary" className="text-[10px]">{img.downloads}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Top Cuisines */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h4 className="text-xs font-semibold text-slate-600 mb-3">Top Cuisines</h4>
            {optional.top_cuisines.length === 0 ? (
              <p className="text-[11px] text-slate-400">No data</p>
            ) : (
              <div className="space-y-2">
                {optional.top_cuisines.map((cuisine, idx) => (
                  <div key={cuisine.name} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">
                      <span className="text-slate-400 mr-1">{idx + 1}.</span>
                      {cuisine.name}
                    </span>
                    <Badge variant="secondary" className="text-[10px]">{cuisine.downloads}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Top Coupons */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h4 className="text-xs font-semibold text-slate-600 mb-3">Top Coupons</h4>
            {optional.top_coupons.length === 0 ? (
              <p className="text-[11px] text-slate-400">No coupons used</p>
            ) : (
              <div className="space-y-2">
                {optional.top_coupons.map((coupon, idx) => (
                  <div key={coupon.code} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">
                      <span className="text-slate-400 mr-1">{idx + 1}.</span>
                      {coupon.code}
                    </span>
                    <div className="flex items-center gap-1">
                      <Badge variant="secondary" className="text-[10px]">{coupon.times_used}x</Badge>
                      <Badge variant="outline" className="text-[10px]">₹{coupon.revenue}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
